import React, { useCallback, useState } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useGridApiContext } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useLazySearchAuditActionsQuery } from '@/features/audit/auditApi';
import { useLazyBatchGetClientsQuery } from '@/features/clients/clientsApi';
import { useLazyBatchGetDealsQuery } from '@/features/deals/dealsApi';
import { useGetBrokerGroupUsersQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { parseValueForCsv } from '@/components/tables/InstantSearchDataGrid/data';
import { enrichAuditRowsForExport, sortAuditRows } from './auditRowUtils';
import { showToast } from '@/features/toast/toastSlice';
import { useTableSort } from '@/context/tableSortContext';

export const AuditExportButton = ({ searchParams, disabled = false }) => {
	const dispatch = useDispatch();
	const apiRef = useGridApiContext();
	const { sortModel } = useTableSort();
	const [loading, setLoading] = useState(false);
	const [searchAuditActions] = useLazySearchAuditActionsQuery();
	const [getBatchClients, { isLoading: isLoadingClients }] = useLazyBatchGetClientsQuery();
	const [getBatchDeals, { isLoading: isLoadingDeals }] = useLazyBatchGetDealsQuery();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const canViewGroup = userDetails?.canViewGroup;

	const brokerGroupUsersQuery = useGetBrokerGroupUsersQuery(
		{ userId: userDetails?.userId, showGroup: true },
		{ skip: !userDetails?.userId || !canViewGroup },
	);

	const brokerGroupUsers = (brokerGroupUsersQuery.data ?? []).reduce((acc, current) => {
		if (!acc.find((item) => item.id === current.id)) {
			acc.push(current);
		}
		return acc;
	}, []);

	const isExporting = loading || isLoadingClients || isLoadingDeals;

	const handleExport = useCallback(async () => {
		setLoading(true);
		try {
			const columns = apiRef.current.getVisibleColumns();
			const response = await searchAuditActions(searchParams).unwrap();
			const rows = response?.data ?? [];

			if (!rows.length) {
				dispatch(showToast({ message: 'No audit records to export' }));
				return;
			}

			const enrichedRows = await enrichAuditRowsForExport({
				rows,
				getBatchClients,
				getBatchDeals,
			});

			const sortedRows = sortAuditRows(enrichedRows, sortModel);
			const csvRows = [
				columns.map((column) => column.headerName).join(','),
				...sortedRows.map((row) => {
					const values = columns.map((column) =>
						parseValueForCsv({
							column,
							brokerGroupUsers,
							value: row,
						}),
					);

					return values.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
				}),
			];

			const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'Audit_Data_Extract.csv';
			link.click();
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Audit export failed', error);
			dispatch(
				showToast({
					message: 'Error running export; please try again.',
					error: true,
				}),
			);
		} finally {
			setLoading(false);
		}
	}, [
		apiRef,
		brokerGroupUsers,
		dispatch,
		getBatchClients,
		getBatchDeals,
		searchAuditActions,
		searchParams,
		sortModel,
	]);

	return (
		<Tooltip title='Export data as CSV'>
			<Box>
				<Button
					disabled={disabled || isExporting}
					variant='text'
					size='small'
					endIcon={
						isExporting ? <CircularProgress size='1em' color='white' /> : <GetAppIcon />
					}
					onClick={handleExport}
				>
					Export
				</Button>
			</Box>
		</Tooltip>
	);
};
